import { css } from '@emotion/react';
import React, { useCallback } from 'react';

import { Phase } from '@mythos/game/Phases';
import { computeBaseCounters, getMaxCounters } from '@mythos/game/Rules';
import ActionStore, { ActionStoreCanSubmitResult } from './ActionStore';
import { ColorPalette, UIColorPalette } from './ColorPalette';
import DieView from './DieView';
import { PlayerCounter } from './PlayerBoardView';
import Symbols, { SymbolView } from './Symbols';

import { TutorialHighlightType } from '@mythos/game/Tutorial';
import GameButton, { GameButtonTheme, makeGameButtonTheme } from './GameButton';
import Spinner from './Spinner';
import Styles from './Styles';
import {
  PlayerSummarySectionHighlightStyle,
  TUTORIAL_HIGHLIGHT_COLOR,
} from './TutorialViews';

export default function SelfPlayerView(props: {
  actionStore: ActionStore;
  ready: boolean;
  phase: Phase;
  rolls?: number[];
  waitingForAnimation?: boolean;
  onSkipAnimation?: () => void;
}) {
  const { rolls, phase, actionStore, ready } = props;
  const onReadyClick = useCallback(() => {
    actionStore.didClickSubmit();
  }, [actionStore]);
  const onUnbidClick = useCallback(() => {
    actionStore.didClickUnbid();
  }, [actionStore]);

  let dice_view: React.ReactNode = null;
  if (rolls && phase === Phase.PLANNING) {
    let die_view = rolls.map((roll, i) => <DieView key={i} roll={roll} />);
    dice_view = <div css={SelfPlayerStyles.diceContainer}>{die_view}</div>;
  }

  let phaseView: React.ReactNode = null;
  let buttonText = 'READY';
  if (phase === Phase.PLANNING) {
    const bid = actionStore.getMilitaryBidOrNan();
    const baseCounters = computeBaseCounters(actionStore.getContext());
    const maxCounters = getMaxCounters(actionStore.getContext());
    const bidLimits = actionStore.getMilitaryBidLimits();

    const tutorialMessage = actionStore.getTutorialMessage();
    const tutorialAction = actionStore.getTutorialData()?.playerPlanningAction;
    const tutorialBid = tutorialAction?.militaryBid;
    const highlightBidInput =
      tutorialBid !== undefined &&
      bid !== tutorialBid &&
      actionStore.shouldShowTutorialUserHighlights();
    const highlightBidContainerResources =
      tutorialMessage?.highlightType === TutorialHighlightType.BIDDER &&
      tutorialMessage.highlightedElements.includes('resources');
    const bidInputStyle = highlightBidInput
      ? {
          boxShadow: `0px 0px 12px 4px ${TUTORIAL_HIGHLIGHT_COLOR}`,
        }
      : {};
    phaseView = (
      <div css={SelfPlayerStyles.bidContainer}>
        <div
          css={[
            SelfPlayerStyles.bidContainerResources,
            highlightBidContainerResources &&
              PlayerSummarySectionHighlightStyle,
          ]}
        >
          <PlayerCounter
            name="gold"
            count={actionStore.getPlayer().counters.gold}
            baseCount={baseCounters.gold}
            maxCount={maxCounters.maxGold}
            showMax
          />
          <PlayerCounter
            name="military"
            count={actionStore.getPlayer().counters.military}
            baseCount={baseCounters.military}
            maxCount={maxCounters.maxMilitary}
            showMax
          />
        </div>
        Bid:
        <>
          <input
            css={SelfPlayerStyles.bidInput}
            style={bidInputStyle}
            name="bid"
            type="number"
            min={bidLimits.min}
            max={bidLimits.max}
            value={isNaN(bid) ? '' : bid}
            onChange={(e) => {
              if (e.target.value === '') {
                actionStore.didChangeMilitary(NaN);
              } else {
                actionStore.didChangeMilitary(parseInt(e.target.value));
              }
            }}
          />
          <SymbolView
            symbol={Symbols.MILITARY}
            css={SelfPlayerStyles.bidSymbol}
          />
        </>
      </div>
    );
  }

  const skipButton = props.waitingForAnimation && (
    <SkipAnimationButton onClick={props.onSkipAnimation} />
  );

  const showUnbidButton = !props.waitingForAnimation && actionStore.canUndo();

  const unbidButton = showUnbidButton && <UndoButton onClick={onUnbidClick} />;
  const readyButton = !props.waitingForAnimation &&
    !showUnbidButton &&
    (phase === Phase.PLANNING || phase === Phase.RESOLUTION) && (
      <PlayerStatusButton
        ready={ready}
        canSubmit={actionStore.canSubmit()}
        onReadyClick={onReadyClick}
      >
        {buttonText}
      </PlayerStatusButton>
    );
  return (
    <div css={SelfPlayerStyles.container}>
      {skipButton}
      {phaseView}
      {dice_view}
      {readyButton}
      {unbidButton}
    </div>
  );
}

const SelfPlayerStyles = {
  container: css({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',

    margin: 5,
    marginBottom: 0,
    paddingLeft: 10,
    paddingRight: 10,
    flex: '0 0 auto',

    borderRadius: 4,
    height: 35,
    minWidth: 200,
    backgroundColor: UIColorPalette.selfPlayerViewBG,
    color: ColorPalette.printBlack,
  }),
  diceContainer: css({
    height: 12,
    minWidth: 44,
    marginLeft: 4,
  }),
  bidContainer: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  bidContainerResources: css(Styles.flexRow, {
    justifyContent: 'center',
  }),
  bidInput: css({
    marginLeft: 4,
    padding: 2,
    paddingLeft: 4,
    width: 40,
    borderRadius: 4,
    border: 'none',
    backgroundColor: 'rgb(202, 202, 202)',
    color: ColorPalette.printBlack,
  }),
  bidSymbol: css({
    height: 20,
    width: 20,
    marginLeft: 2,
  }),
};

function UndoButton(props: { onClick: () => void }) {
  return (
    <GameButton
      theme={GameButtonTheme.SelfPlayerView}
      css={[PlayerStatusButtonStyles.button, PlayerStatusButtonStyles.undo]}
      onClick={props.onClick}
    >
      Undo
    </GameButton>
  );
}

function PlayerStatusButton(props: {
  ready: boolean;
  canSubmit: ActionStoreCanSubmitResult;
  onReadyClick: () => void;
  children?: React.ReactNode;
}) {
  return props.ready ? (
    <div css={PlayerStatusButtonStyles.ready}>{props.children}</div>
  ) : (
    <div css={PlayerStatusButtonStyles.container}>
      <GameButton
        theme={GameButtonTheme.SelfPlayerView}
        css={PlayerStatusButtonStyles.button}
        onClick={props.onReadyClick}
        disabled={!props.canSubmit.result}
      >
        {props.children}
      </GameButton>
      {props.canSubmit.message && (
        <div
          css={PlayerStatusButtonStyles.cantSubmitMessage}
          className="cant-submit-message"
        >
          {props.canSubmit.message}
        </div>
      )}
    </div>
  );
}
function PlayerRerollButton(props: {
  className?: string;
  style?: React.CSSProperties;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      className={props.className}
      style={props.style}
      onClick={props.onClick}
    >
      {props.children}
    </button>
  );
}
const BUTTON_HEIGHT = 19;
const BUTTON_WIDTH = 113;
const PlayerStatusButtonStyles = {
  container: css({
    position: 'relative',
    '&:hover .cant-submit-message': {
      visibility: 'visible',
    },
  }),
  button: css({
    fontSize: '10px',
    padding: '2px',

    // display: 'flex',
    // flexDirection: 'column',
    // alignItems: 'center',

    width: BUTTON_WIDTH,
    height: BUTTON_HEIGHT,
    margin: 4,
    textAlign: 'center',
    position: 'relative',
  }),
  undo: css({
    background: 'rgb(71, 128, 69)',
    borderColor: ColorPalette.printBlack,
    textColor: ColorPalette.printBlack,
    '&:hover:enabled': {
      background: 'rgb(87, 156, 85)',
    },
    '&:active:enabled': {
      background: 'rgb(54, 97, 52)',
    },
    '&:disabled': {
      background: 'rgb(51, 96, 48)',
    },
  }),
  ready: css({
    padding: 2,
    fontSize: 10,
    marginLeft: 10,
    backgroundColor: UIColorPalette.statusButtonGreen,
    border: 'none',
    height: '15px',
    lineHeight: '15px',
    width: '110px',
    color: ColorPalette.printBlack,
    textAlign: 'center',
    borderRadius: 4,
  }),
  cantSubmitMessage: css({
    position: 'absolute',
    top: '100%',
    left: 0,

    visibility: 'hidden',
    padding: 4,
    fontSize: 14,
    marginTop: 4,
    color: UIColorPalette.cantSubmitText,
    backgroundColor: UIColorPalette.cantSubmitBG,
    borderRadius: 4,
    zIndex: 10,
  }),
};

function SkipAnimationButton(props: { onClick?: () => void }) {
  return (
    <GameButton
      theme={GameButtonTheme.TutorialNext}
      css={SkipAnimationButtonStyles.root}
      onClick={props.onClick}
    >
      <Spinner
        size={12}
        borderWidth={2}
        color={ColorPalette.printBlack}
        trackColor={ColorPalette.dimText}
      />
      SKIP
    </GameButton>
  );
}
const SkipAnimationButtonStyles = {
  root: css(
    {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      gap: 4,

      width: BUTTON_WIDTH,
      height: BUTTON_HEIGHT,

      margin: 4,
      outline: 'none',
    },
    makeGameButtonTheme({
      textColor: ColorPalette.printBlack,
      background: 'rgb(143, 159, 191)',
      hoverBackground: 'rgb(161, 179, 214)',
      activeBackground: 'rgb(163, 182, 200)',
    }),
  ),
};
